/**
 * Contact Us Page Component
 * Allows users to submit feedback, questions, or report errors to UBT administrators
 * Follows React 17+ and TypeScript 5+ best practices with SOLID principles
 * @module pages/ContactUs
 */

import React, { FC, useRef, useCallback, useMemo } from "react";
import { useNavigate } from "react-router";
import BreadCrumbs from "../../../components/common/BreadCrumbs/BreadCrumbs";
import Dropdown from "../../../components/common/Dropdown";
import ScrollToTopBtn from "../../../components/common/ScrollToTopBtn";
import TextAreaWithCounter from "../../../components/common/TextAreaWithCounter";
import ErrorBanner, { ValidationError } from "../../../components/common/ErrorBanner";
import Notifications from "../../../components/common/Notifications";
import Loader from "../../../components/common/Loader";
import {
  ReadOnlyInputField,
  RequiredLabel,
  FormFieldWrapper,
  TextInputField,
} from "../../../components/common/FormFields";
import { useContactData } from "../../../hooks/contact/useContactData";
import { useContactForm } from "../../../hooks/contact/useContactForm";
import {
  PAGE_CONTENT,
  FIELD_LABELS,
  FIELD_PLACEHOLDERS,
  BUTTON_TEXT,
  HELPER_TEXT,
  FIELD_LIMITS,
  TIMEOUTS,
} from "../../../constants/contact.constants";
import { isValidEmail } from "../../../utils/ContactUs/contactUsValidation";
import "form-pattern/dist/main.css";

/**
 * ContactUs Component
 * Main contact form page with cascading dropdowns, validation, and form submission
 */
const ContactUs: FC = () => {
  const navigate = useNavigate();

  // Refs for error navigation and scroll behavior
  const pageTopRef = useRef<HTMLHeadingElement>(null);
  const regionRef = useRef<HTMLDivElement>(null);
  const centerRef = useRef<HTMLDivElement>(null);
  const topicRef = useRef<HTMLDivElement>(null);
  const teamNameRef = useRef<HTMLInputElement>(null);
  const emailRef = useRef<HTMLInputElement>(null);
  const phoneAreaCodeRef = useRef<HTMLInputElement>(null);
  const phoneNumberRef = useRef<HTMLInputElement>(null);
  const commentsRef = useRef<HTMLDivElement>(null);

  /**
   * Scroll to top of page
   */
  const scrollToTop = useCallback(() => {
    setTimeout(() => {
      if (pageTopRef.current) {
        pageTopRef.current.scrollIntoView({ behavior: "smooth", block: "start" });
      } else {
        window.scrollTo({ top: 0, behavior: "smooth" });
      }
    }, TIMEOUTS.SCROLL_DELAY);
  }, []);

  // Custom hooks for data fetching and form management
  const {
    userDetails,
    regionOptions,
    centerOptions,
    topicOptions,
    isLoadingInitialData,
    isLoadingCenters,
    isLoadingTopics,
    dataError,
    loadCenters,
    loadTopics,
  } = useContactData();

  const {
    selectedRegion,
    selectedCenter,
    selectedTopic,
    teamNameOrUbtId,
    phoneAreaCode,
    phoneNumber,
    comments,
    email,
    validationErrors,
    hasAttemptedSubmit,
    successMessage,
    errorMessage,
    isSubmitting,
    setSelectedRegion,
    setSelectedCenter,
    setSelectedTopic,
    setTeamNameOrUbtId,
    setPhoneAreaCode,
    setPhoneNumber,
    setComments,
    setEmail,
    handleSubmit,
    handleClear,
  } = useContactForm({
    userDetails,
    onRegionChange: (regionCode: string) => { void loadCenters(regionCode); },
    onCenterChange: (regionCode: string, centerCode: string) => { void loadTopics(regionCode, centerCode); },
    onScrollToTop: scrollToTop,
  });

  /**
   * Handle breadcrumb navigation
   */
  const handleBreadcrumbPrevious = useCallback((): void => {
    navigate("/home");
  }, [navigate]);

  /**
   * Map validation errors to ErrorBanner format with refs
   */
  const errorBannerErrors = useMemo<ValidationError[]>(() => {
    const refMap: Record<string, React.RefObject<HTMLElement>> = {
      region: regionRef as React.RefObject<HTMLElement>,
      center: centerRef as React.RefObject<HTMLElement>,
      topic: topicRef as React.RefObject<HTMLElement>,
      teamNameOrUbtId: teamNameRef as React.RefObject<HTMLElement>,
      email: emailRef as React.RefObject<HTMLElement>,
      phoneAreaCode: phoneAreaCodeRef as React.RefObject<HTMLElement>,
      phoneNumber: phoneNumberRef as React.RefObject<HTMLElement>,
      comments: commentsRef as React.RefObject<HTMLElement>,
    };

    return validationErrors.map((error) => ({
      field: error.field,
      message: error.message,
      fieldRef: refMap[error.field],
    }));
  }, [validationErrors]);

  /**
   * Get error message for a field
   */
  const getErrorMessage = useCallback((fieldName: string): string | undefined => {
    return validationErrors.find((error) => error.field === fieldName)?.message;
  }, [validationErrors]);

  /**
   * Combined loading state
   */
  const isLoading = isLoadingCenters || isLoadingTopics || isSubmitting;

  /**
   * Display error message
   */
  const displayError = dataError || errorMessage;

  // Show loading screen while fetching initial data
  if (isLoadingInitialData) {
    return (
      <div className="min-h-[70vh] lg:mx-40! mx-5!">
        <div className="flex items-center justify-center h-[70vh]">
          <Loader />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-[70vh] lg:mx-40! mx-5!">
      <BreadCrumbs
        previousPage={PAGE_CONTENT.BREADCRUMB_PREVIOUS}
        onPreviousClick={handleBreadcrumbPrevious}
      />

      <div>
        <h1 ref={pageTopRef} className="h2 mt-6!">
          {PAGE_CONTENT.TITLE}
        </h1>
        <p className="p2 -medium mt-6!">{PAGE_CONTENT.DESCRIPTION}</p>

        {/* Loading indicator */}
        {isLoading && (
          <div className="mt-8 flex justify-center">
            <Loader />
          </div>
        )}

        {/* Messages */}
        {successMessage && (
          <div className="mt-10!">
            <Notifications
              variant="Success"
              heading="Thank you for your feedback."
              messageInfo={successMessage}
            />
          </div>
        )}

        {displayError && (
          <div className="mt-10!">
            <Notifications variant="Error" heading="Error" messageInfo={displayError} />
          </div>
        )}

        {errorBannerErrors.length > 0 && (
          <div className="mt-10!">
            <ErrorBanner errors={errorBannerErrors} />
          </div>
        )}

        {/* Contact Form */}
        <form
          onSubmit={handleSubmit}
          className="flex flex-col gap-8 flex-wrap mt-7.5! w-full"
          noValidate
        >
          <div className="grid grid-cols-1 desktop:grid-cols-2 gap-x-4! gap-y-8 w-full">
            {/* Read-only user details */}
            <ReadOnlyInputField
              label={FIELD_LABELS.NUID}
              value={userDetails?.nuid ?? ""}
              id="nuid"
            />

            <ReadOnlyInputField
              label={FIELD_LABELS.FIRST_NAME}
              value={userDetails?.firstName ?? ""}
              id="firstName"
            />

            <ReadOnlyInputField
              label={FIELD_LABELS.LAST_NAME}
              value={userDetails?.lastName ?? ""}
              id="lastName"
            />

            {/* Region Dropdown */}
            <FormFieldWrapper
              label={<RequiredLabel>{FIELD_LABELS.REGION}</RequiredLabel>}
              id="region"
              error={hasAttemptedSubmit ? getErrorMessage("region") : undefined}
              helperText={HELPER_TEXT.REQUIRED_FIELD}
              showHelper={Boolean(selectedRegion.value)}
              fieldRef={regionRef}
            >
              <Dropdown
                selectedValue={selectedRegion}
                buttonClassName="w-full!"
                menuClassName="w-full! max-h-60! overflow-y-auto!"
                options={regionOptions}
                onSelect={setSelectedRegion}
                placeholder={FIELD_PLACEHOLDERS.SELECT}
                aria-label={FIELD_LABELS.REGION}
              />
            </FormFieldWrapper>

            {/* Center Dropdown */}
            <FormFieldWrapper
              label={<RequiredLabel>{FIELD_LABELS.CENTER}</RequiredLabel>}
              id="center"
              error={hasAttemptedSubmit ? getErrorMessage("center") : undefined}
              helperText={HELPER_TEXT.REQUIRED_FIELD}
              showHelper={Boolean(selectedCenter.value)}
              fieldRef={centerRef}
            >
              <Dropdown
                selectedValue={selectedCenter}
                buttonClassName="w-full!"
                menuClassName="w-full! max-h-60! overflow-y-auto!"
                options={centerOptions}
                onSelect={setSelectedCenter}
                placeholder={FIELD_PLACEHOLDERS.SELECT}
                disabled={!selectedRegion.value}
                aria-label={FIELD_LABELS.CENTER}
              />
            </FormFieldWrapper>

            {/* Team Name / UBT ID */}
            <TextInputField
              label={<RequiredLabel>{FIELD_LABELS.TEAM_NAME_UBT_ID}</RequiredLabel>}
              id="teamNameOrUbtId"
              name="teamNameOrUbtId"
              value={teamNameOrUbtId}
              onChange={(e) => setTeamNameOrUbtId(e.target.value)}
              placeholder={FIELD_PLACEHOLDERS.TEAM_NAME_UBT_ID}
              error={hasAttemptedSubmit ? getErrorMessage("teamNameOrUbtId") : undefined}
              helperText={HELPER_TEXT.REQUIRED_FIELD}
              showHelper={Boolean(teamNameOrUbtId.trim())}
              fieldRef={teamNameRef}
            />

            {/* Phone Area Code */}
            <TextInputField
              label={FIELD_LABELS.PHONE_AREA_CODE}
              id="phoneAreaCode"
              name="phoneAreaCode"
              value={phoneAreaCode}
              onChange={(e) => setPhoneAreaCode(e.target.value)}
              placeholder={FIELD_PLACEHOLDERS.PHONE_AREA_CODE}
              type="tel"
              maxLength={FIELD_LIMITS.PHONE_AREA_CODE_LENGTH}
              error={hasAttemptedSubmit ? getErrorMessage("phoneAreaCode") : undefined}
              fieldRef={phoneAreaCodeRef}
            />

            {/* Phone Number */}
            <TextInputField
              label={FIELD_LABELS.PHONE_NUMBER}
              id="phoneNumber"
              name="phoneNumber"
              value={phoneNumber}
              onChange={(e) => setPhoneNumber(e.target.value)}
              placeholder={FIELD_PLACEHOLDERS.PHONE_NUMBER}
              type="tel"
              maxLength={FIELD_LIMITS.PHONE_NUMBER_MAX_INPUT_LENGTH}
              error={hasAttemptedSubmit ? getErrorMessage("phoneNumber") : undefined}
              fieldRef={phoneNumberRef}
            />

            {/* Email */}
            <TextInputField
              label={<RequiredLabel>{FIELD_LABELS.EMAIL}</RequiredLabel>}
              id="email"
              name="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder={FIELD_PLACEHOLDERS.EMAIL}
              type="email"
              error={hasAttemptedSubmit ? getErrorMessage("email") : undefined}
              helperText={HELPER_TEXT.REQUIRED_FIELD}
              showHelper={Boolean(email.trim() && isValidEmail(email))}
              fieldRef={emailRef}
            />

            {/* Topic Dropdown */}
            <FormFieldWrapper
              label={<RequiredLabel>{FIELD_LABELS.TOPIC}</RequiredLabel>}
              id="topic"
              error={hasAttemptedSubmit ? getErrorMessage("topic") : undefined}
              helperText={HELPER_TEXT.REQUIRED_FIELD}
              showHelper={Boolean(selectedTopic.value)}
              fieldRef={topicRef}
            >
              <Dropdown
                selectedValue={selectedTopic}
                buttonClassName="w-full!"
                menuClassName="w-full! max-h-60! overflow-y-auto!"
                options={topicOptions}
                onSelect={setSelectedTopic}
                placeholder={FIELD_PLACEHOLDERS.SELECT}
                disabled={!selectedCenter.value}
                aria-label={FIELD_LABELS.TOPIC}
              />
            </FormFieldWrapper>
          </div>

          {/* Comments */}
          <div className="form-group w-full">
            <label htmlFor="comments">
              <RequiredLabel>{FIELD_LABELS.COMMENTS}</RequiredLabel>
            </label>
            <div ref={commentsRef}>
              <TextAreaWithCounter
                className="max-w-full! text-[16px]!"
                id="comments"
                value={comments}
                placeholder={FIELD_PLACEHOLDERS.COMMENTS}
                maxLength={FIELD_LIMITS.COMMENTS_MAX_LENGTH}
                onChange={(value: string) => setComments(value)}
                isRequired
              />
            </div>
            {hasAttemptedSubmit && getErrorMessage("comments") && (
              <div className="error-description">
                <ul id="comments-error">
                  <li>
                    <img
                      src="/assets/images/errorCircleSmall.svg"
                      alt="Error icon"
                      width="14"
                      height="14"
                    />
                    <span className="screenreader-only">{getErrorMessage("comments")}</span>{" "}
                    {getErrorMessage("comments")}
                  </li>
                </ul>
              </div>
            )}
          </div>

          {/* Form Actions */}
          <div className="flex flex-col-reverse gap-4! desktop:gap-3! desktop:flex-row mt-12 w-full">
            <button
              type="button"
              className="button -light m-0! w-full! desktop:w-auto! self-stretch flex items-center justify-center box-border max-w-none"
              onClick={handleClear}
              disabled={isLoading}
              aria-label="Clear form"
            >
              <span className="flex items-center justify-center w-full">
                {BUTTON_TEXT.CLEAR}
              </span>
            </button>
            <button
              type="submit"
              className="button -primary -light m-0! w-full! desktop:w-auto! self-stretch flex items-center justify-center box-border max-w-none!"
              disabled={isLoading}
              aria-label={isSubmitting ? "Sending message" : "Send message"}
            >
              <span className="flex items-center justify-center w-full">
                {isSubmitting ? BUTTON_TEXT.SENDING : BUTTON_TEXT.SEND}
              </span>
            </button>
          </div>
        </form>
      </div>

      <ScrollToTopBtn />
    </div>
  );
};

export default ContactUs;
